import type { SuperDocInstance, SuperDocRuntimeEditor } from '@superdoc/react';
import {
  CANVAS_DRAFT_DEFAULT_TITLE,
  cleDeMontage,
  type CanvasDocumentDepart,
  type CanvasDraft,
  type StructuredBlock,
} from '@hector/shared-types';
import type { CanvasWriteRequestMetadata, ResultatEcriture } from '../editor/canvasWritePoint';
import type { EnTeteAPoser } from '../editor/canvasLetterhead';
import {
  CanvasDraftSessionController,
  type CanvasDraftControllerDependencies,
  type CanvasDraftSessionContext,
} from './canvasDraftController';
import type { CanvasTemplateMeta } from './canvasEditorStore';
import { EPOQUE_AVANT_MONTAGE } from './canvasSessionRegistry';
import { alignerLeModeSurLeRegime } from './modeDuDocument';
import { lireLaRevisionDuDocument } from './canvas-agent/fencesDeMutation';

/**
 * Zoom d'ouverture du canvas (%). 80 % : arbitrage produit du 2026-07-16 (la
 * page A4 tient dans le split chat/canvas). SEULE définition depuis #1329 : un
 * doublon à 70 % vivait dans `canvasEditorStore` avec une demande produit du
 * 2026-08-25 (lisibilité sur écrans étroits) qui n'a jamais été appliquée, faute
 * de lecteur — décision à reprendre dans #1341, pas ici en silence.
 */
export const REDACTION_DEFAULT_ZOOM_PERCENT = 80;

/**
 * Zoom d'ouverture en fenêtre étroite (< 768 px, audit mobile 2026-10-22). Une
 * page A4 fait ~794 px à 100 % : à 80 % elle débordait de part et d'autre d'un
 * téléphone, où le canvas prend tout l'écran (onglet « Document »). 45 % la fait
 * tenir dans ~360 px ; l'avocat zoome ensuite à son gré. Lu une fois, à la
 * construction du scope : un document ouvert en fenêtre étroite reste à 45 % si
 * la fenêtre s'agrandit ensuite, jusqu'au document suivant.
 */
export const REDACTION_NARROW_ZOOM_PERCENT = 45;

/** Zoom d'ouverture selon la largeur de la fenêtre, lue au montage du document. */
export function zoomDOuvertureDuCanvas(): number {
  const etroite =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 767px)').matches;
  return etroite ? REDACTION_NARROW_ZOOM_PERCENT : REDACTION_DEFAULT_ZOOM_PERCENT;
}

export type CanvasDocumentWriter = (
  blocks: readonly StructuredBlock[],
  metadata?: CanvasWriteRequestMetadata,
) => Promise<ResultatEcriture>;

/** Resolution outcome for the named-letterhead canvas action. */
export type CanvasNamedLetterheadResolution =
  | { readonly issue: 'trouve'; readonly nom: string; readonly entree: EnTeteAPoser }
  | { readonly issue: 'introuvable'; readonly disponibles: readonly string[] }
  | { readonly issue: 'ambigu'; readonly correspondances: readonly string[] }
  | { readonly issue: 'sans-fichier'; readonly nom: string };

/** React-owned resolver captured by exactly one mounted document session. */
export type CanvasNamedLetterheadResolver = (
  nom: string | null,
) => Promise<CanvasNamedLetterheadResolution>;

export interface CanvasDocumentPhysicalResources {
  readonly instance: SuperDocInstance | null;
  readonly editor: SuperDocRuntimeEditor | null;
  readonly writer: CanvasDocumentWriter | null;
  readonly namedLetterheadResolver: CanvasNamedLetterheadResolver | null;
  readonly zoomPercent: number;
  readonly reviewEnabled: boolean;
}

export interface CanvasDocumentDomResources {
  host: HTMLDivElement;
  parking: HTMLDivElement;
  hostId: string;
  toolbarId: string;
  scrollId: string;
  documentId: string;
}

/**
 * **Le document de cette conversation, et rien d'autre** (#1333).
 *
 * Ce n'était qu'une copie gelée d'une projection globale tant que le store
 * `canvasEditorStore` portait la présentation documentaire de l'onglet. Cette
 * projection a été supprimée : le scope est désormais la seule autorité, ses
 * consommateurs l'observent directement, et rien ne recopie plus une
 * présentation d'un document à l'autre au fil de la navigation.
 *
 * ⚠️ Ce type ne porte PAS le statut de la recherche de brouillon : le tri-état
 * de restauration vit dans `canvasRestaurationDeBrouillon`, indexé par
 * conversation. Le rapatrier ici en ferait une seconde autorité.
 */
export interface CanvasDocumentPresentationSnapshot {
  readonly templateOverride: Blob | null;
  readonly templateMeta: CanvasTemplateMeta | null;
  readonly documentTitle: string | null;
  readonly activeDraft: CanvasDraft | null;
  readonly draftNeedsHydration: boolean;
  /**
   * Sur quoi part cette page NEUVE ; absent = le défaut du cabinet.
   *
   * C'est une réponse portée jusqu'à l'effet de chargement de `RedactionCanvas`, qui la lit
   * une fois au montage : ni un document, ni un `templateOverride` (le papier n'en devient
   * jamais un, voir `editor/documentDeDepart.ts`). Elle reste sur la présentation tant que le
   * document n'est pas remplacé, pour qu'un remontage de la même page neuve reparte du même
   * papier et que la pose automatique du logo continue de s'abstenir.
   */
  readonly depart?: CanvasDocumentDepart;
}

/**
 * Ce qu'un brouillon durable devient, comme document affiché.
 *
 * ⚠️ **On ne restaure jamais « Document sans titre » comme titre choisi** :
 * c'est le défaut du schéma partagé (`CanvasDraftSchema`), et le prendre pour
 * un choix de l'avocat afficherait un renommage qui n'a pas eu lieu — le
 * document doit retomber sur le libellé dérivé de son état.
 *
 * Une seule définition, lue par la préparation de la session logique
 * (`canvasDocumentRuntime.prepareRestoredDraftSession`). Il n'existe plus de
 * publication vers un scope déjà monté (#1586) : la restauration refuse un fil
 * dont le document est monté, donc cette présentation n'entre dans un éditeur
 * que par la transaction de résidence.
 */
export function presentationDUnBrouillonRestaure(
  draft: CanvasDraft,
): CanvasDocumentPresentationSnapshot {
  return {
    templateOverride: null,
    templateMeta: {
      documentId: draft.sourceDocumentId,
      documentTitle: draft.title,
      conversationId: draft.conversationId,
      caseId: draft.caseId,
      sourceFilePath: null,
      kind: draft.kind,
    },
    documentTitle: draft.title && draft.title !== CANVAS_DRAFT_DEFAULT_TITLE ? draft.title : null,
    activeDraft: draft,
    draftNeedsHydration: true,
  };
}

/** Un document sur lequel rien n'a été posé : ni gabarit, ni titre, ni brouillon. */
export const PRESENTATION_VIERGE: CanvasDocumentPresentationSnapshot = Object.freeze({
  templateOverride: null,
  templateMeta: null,
  documentTitle: null,
  activeDraft: null,
  draftNeedsHydration: false,
});

/**
 * La présentation d'une page neuve partie de `depart`.
 *
 * `defaut` rend la constante, sans champ : « absent » est la seule écriture du défaut, et les
 * lecteurs qui comparent une présentation à `PRESENTATION_VIERGE` gardent leur référence.
 */
export function presentationDUnePageNeuve(depart?: CanvasDocumentDepart): CanvasDocumentPresentationSnapshot {
  return depart === undefined || depart.kind === 'defaut' ? PRESENTATION_VIERGE : { ...PRESENTATION_VIERGE, depart };
}

/**
 * Une présentation traverse le parking et peut survivre à plusieurs rendus.
 * Ne jamais conserver par référence les objets imbriqués fournis par un
 * appelant : un changement tardif de méta ou de brouillon ferait alors muter le
 * résident parqué, donc un autre document que celui du geste initial.
 */
function clonePresentation(
  presentation: CanvasDocumentPresentationSnapshot,
): CanvasDocumentPresentationSnapshot {
  return {
    ...presentation,
    templateMeta: presentation.templateMeta ? { ...presentation.templateMeta } : null,
    activeDraft: presentation.activeDraft ? { ...presentation.activeDraft } : null,
  };
}

/**
 * Suffixe DOM du DOCUMENT, pas de son montage : les identifiants `id=` du host,
 * de la toolbar et du conteneur de défilement sont créés une fois et survivent
 * aux remontages. Le dériver de l'époque les ferait changer sous les sélecteurs
 * qui les tiennent.
 */
function scopeDomSuffix(conversationId: string): string {
  let hash = 2166136261;
  for (const char of conversationId) {
    hash ^= char.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  const readable = conversationId.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 48) || 'session';
  return `${readable}-${(hash >>> 0).toString(16)}`;
}

export type CanvasDocumentScopeState = 'loading' | 'ready' | 'failed' | 'disposed';

/**
 * Ce qu'un scope montre du montage qu'il incarne.
 *
 * ⚠️ **`(conversationId, mountEpoch)` est l'identité** (#1336) : plus de
 * `documentSessionId`, et `conversationId` n'est plus nullable. Un scope EST un
 * montage, et un montage sans propriétaire ne désignerait rien — c'était la
 * fenêtre que #1334 avait fermée côté publication et que ce lot ferme côté type.
 */
export interface CanvasDocumentMountIdentity {
  conversationId: string;
  mountEpoch: number;
  documentId: string | null;
}

export interface CanvasDocumentScopeSnapshot extends CanvasDocumentMountIdentity {
  instance: SuperDocInstance | null;
  editor: SuperDocRuntimeEditor | null;
  revision: string | null;
  state: CanvasDocumentScopeState;
  /**
   * Pourquoi ce montage est en échec ; `null` dans tout autre état. C'est le
   * seul récit de l'échec qui quitte le navigateur : les messages rendus à
   * l'agent le citent.
   */
  failure: string | null;
}

export interface CanvasDocumentLogicalSeed {
  readonly identity: CanvasDocumentMountIdentity;
  readonly presentation: CanvasDocumentPresentationSnapshot | null;
}

type EditorWithDoc = SuperDocRuntimeEditor & {
  doc?: Parameters<typeof lireLaRevisionDuDocument>[0];
};

/**
 * Le message d'une exception de l'éditeur n'a pas de taille connue, et il part
 * vers l'agent et les journaux du serveur.
 */
const LONGUEUR_MAX_DE_LA_CAUSE = 300;

/** La cause d'un montage en échec : l'exception de la lecture de révision, ou le fait qu'elle n'en a rendu aucune. */
function decrireLEchecDuMontage(lecture: { readonly erreur: unknown } | 'sans-revision'): string {
  if (lecture === 'sans-revision') return 'editor.doc.info({}) answered without a revision';
  const { erreur } = lecture;
  const cause = erreur instanceof Error ? `${erreur.name}: ${erreur.message}` : String(erreur);
  return cause.length > LONGUEUR_MAX_DE_LA_CAUSE ? `${cause.slice(0, LONGUEUR_MAX_DE_LA_CAUSE)}…` : cause;
}

/** La révision qui qualifie un montage : le même lecteur que le verrou des écritures. */
async function readRevision(editor: SuperDocRuntimeEditor): Promise<string | null> {
  const doc = (editor as EditorWithDoc).doc;
  if (!doc) return null;
  return (await lireLaRevisionDuDocument(doc)) ?? null;
}

/**
 * Ressources vivantes d'un document dans l'onglet.
 *
 * Le scope ne connaît aucune notion de document « courant » : son identité est
 * fixée par sa `conversationId`, et chaque callback porte l'époque du montage
 * qui l'a émis. C'est cette frontière qui permet au directory d'héberger A et B
 * sans qu'un signal de l'un puisse promouvoir l'autre.
 */
export class CanvasDocumentScope {
  private snapshot: CanvasDocumentScopeSnapshot;
  private promotion: Promise<void> | null = null;
  private mutationQueue: Promise<unknown> = Promise.resolve();
  private mutationGeneration = 0;
  private physical: CanvasDocumentPhysicalResources = {
    instance: null,
    editor: null,
    writer: null,
    namedLetterheadResolver: null,
    zoomPercent: zoomDOuvertureDuCanvas(),
    // Activé à l'ouverture d'un acte (décision du porteur, 2026-10-09) : sans
    // marque de révision, l'avocat ne retrouve pas les corrections de l'agent
    // dans un acte de 50 pages. Il coupe le suivi par le bouton, ou le demande
    // à l'agent.
    reviewEnabled: true,
  };
  private readonly physicalListeners = new Set<() => void>();
  private readonly presentationListeners = new Set<() => void>();
  private readonly snapshotListeners = new Set<(snapshot: CanvasDocumentScopeSnapshot) => void>();
  private readonly disposers = new Set<() => void>();
  private dom: CanvasDocumentDomResources | null = null;
  /** Position du viewport propre à ce document, capturée avant parking. */
  private parkedScrollTop: number | null = null;
  private presentation: CanvasDocumentPresentationSnapshot | null = null;
  private coldHydratedDraftId: string | null = null;
  /**
   * L'état du peintre de l'éditeur monté. Il a la durée de vie de l'INSTANCE,
   * pas celle du régime de suivi : un éditeur neuf peint — il repart à faux à
   * chaque remontage et à chaque instance neuve.
   */
  private peintreArrete = false;
  /** Dernier travail affiché par un éditeur de l'époque VIVANTE — voir `ancrerLeTravailAuMontage`. */
  private travailAffiche: string | null = null;
  /** Borne de l'époque courante : le travail que le document REMPLACÉ affichait. */
  private borneDeLEpoque: string | null = null;
  /**
   * Ce qui a DÉJÀ été lié sous l'époque courante (D1 de #1337) — remis à zéro
   * par `remount`, jamais par `releaseMount` : un hôte démonté puis recréé sous la
   * même époque doit se voir, voir `incarneUnAutreEditeur`.
   */
  private liesALEpoque = { instance: false, editor: false };
  private readonly draftController: CanvasDraftSessionController;
  /** Posée par `RedactionCanvas` : vrai tant que son point d'écriture a un maillon en vol (#1404). */
  private writeInFlightProbe: (() => boolean) | null = null;

  constructor(
    identity: CanvasDocumentMountIdentity,
    draftController = new CanvasDraftSessionController({
      conversationId: identity.conversationId,
      caseId: null,
      kind: 'generated',
      title: CANVAS_DRAFT_DEFAULT_TITLE,
      sourceDocumentId: null,
    }),
  ) {
    this.snapshot = {
      ...identity,
      instance: null,
      editor: null,
      revision: null,
      state: 'loading',
      failure: null,
    };
    this.draftController = draftController;
  }

  get conversationId(): string {
    return this.snapshot.conversationId;
  }

  getSnapshot(): CanvasDocumentScopeSnapshot {
    return { ...this.snapshot };
  }

  getPhysicalResources(): CanvasDocumentPhysicalResources {
    return this.physical;
  }

  getDraftController(): CanvasDraftSessionController {
    return this.draftController;
  }

  /**
   * Publie la présentation de CE document.
   *
   * ⚠️ La valeur rendue par `getPresentation()` est ensuite **stable par
   * référence** jusqu'à la publication suivante. C'est une exigence de
   * `useSyncExternalStore`, qui compare les instantanés par identité : rendre
   * un clone neuf à chaque lecture ferait boucler le rendu du panneau. Le clone
   * est donc fait ici, à l'écriture, une fois — ce qui préserve la raison d'être
   * d'origine (aucun objet d'appelant conservé par référence).
   */
  capturePresentation(
    presentation: CanvasDocumentPresentationSnapshot,
  ): CanvasDocumentPresentationSnapshot {
    this.presentation = clonePresentation(presentation);
    this.notifyPresentation();
    return this.presentation;
  }

  getPresentation(): CanvasDocumentPresentationSnapshot | null {
    return this.presentation;
  }

  /**
   * Ce que ce document affiche quand rien n'a encore été publié pour lui.
   *
   * Une constante partagée, et c'est suffisant depuis que la présentation ne
   * porte plus de version de montage (#1337, lot T) : il n'y a plus rien de
   * propre à ce scope à y recopier. Stable par référence, comme l'exige
   * `useSyncExternalStore` — un littéral neuf à chaque lecture ferait boucler
   * le rendu.
   */
  getPresentationOrEmpty(): CanvasDocumentPresentationSnapshot {
    return this.presentation ?? PRESENTATION_VIERGE;
  }

  subscribePresentation(listener: () => void): () => void {
    this.presentationListeners.add(listener);
    return () => this.presentationListeners.delete(listener);
  }

  /** Records that this mount already received the newest durable DOCX. */
  /**
   * La borne de la garde d'époque de job (`canvasWritePolicy.jobEstPerime`) pour
   * le montage `mountEpoch` : le dernier travail qu'affichait le document que ce
   * montage a REMPLACÉ (#1337). Au passage, retient `jobCourant` comme travail
   * affiché par le montage vivant.
   *
   * Pourquoi « le document remplacé » et pas « le travail affiché à la naissance
   * du montage » (première version du lot N, mesurée fausse au gate du
   * 2026-09-10) : un montage qui n'a RIEN remplacé — le premier d'une
   * conversation, ouvert par un `redaction_job_started` sans admission — est né
   * POUR le travail affiché ; le borner sur lui le jugeait périmé, et le flux
   * n'était jamais écrit (quatorze specs Chromium rouges, texte absent du
   * document peint). Un travail n'a pu écrire que dans un document qui a
   * EXISTÉ avant ce montage : sans prédécesseur, la borne est nulle.
   *
   * Ce que la borne garde toujours : l'admission monte le vierge d'une génération
   * pendant que le plan est encore affiché ; le plan a écrit dans le document
   * remplacé, il ne doit jamais être rejoué dans le vierge, et l'acte suivant ne
   * s'écrit pas à sa suite.
   *
   * ⚠️ Ancrée sur le MONTAGE, pas sur l'instance React : un éditeur recréé sous
   * la même époque (limite d'erreur, hôte recréé) retrouve la même borne, donc
   * réécrit le travail en cours depuis zéro. Un hôte d'une époque DÉPASSÉE (déjà
   * remplacé, pas encore démonté) reçoit son travail courant comme borne, donc
   * n'écrit rien — et ne déplace ni le travail affiché ni la borne du vivant.
   */
  ancrerLeTravailAuMontage(mountEpoch: number, jobCourant: string | null): string | null {
    if (mountEpoch !== this.snapshot.mountEpoch) return jobCourant;
    if (jobCourant !== null) this.travailAffiche = jobCourant;
    return this.borneDeLEpoque;
  }

  /** Le travail que le montage vivant affiche — ce que le foyer froid garde au refroidissement. */
  travailDejaAffiche(): string | null {
    return this.travailAffiche;
  }

  /**
   * Réhydratation depuis le foyer froid : le document rechargé porte déjà ce
   * travail, qui devient donc aussi la borne — il ne doit pas y être rejoué.
   */
  heriterDuTravailDejaAffiche(jobId: string | null): void {
    this.travailAffiche = jobId;
    this.borneDeLEpoque = jobId;
  }

  markColdHydratedDraft(draftId: string | null): void {
    this.coldHydratedDraftId = draftId;
  }

  wasColdHydratedFromDraft(draftId: string): boolean {
    return this.coldHydratedDraftId === draftId;
  }

  subscribePhysical(listener: () => void): () => void {
    this.physicalListeners.add(listener);
    return () => this.physicalListeners.delete(listener);
  }

  subscribeSnapshot(listener: (snapshot: CanvasDocumentScopeSnapshot) => void): () => void {
    this.snapshotListeners.add(listener);
    return () => this.snapshotListeners.delete(listener);
  }

  registerDisposer(disposer: () => void): () => void {
    if (this.snapshot.state === 'disposed') {
      disposer();
      return () => undefined;
    }
    this.disposers.add(disposer);
    return () => this.disposers.delete(disposer);
  }

  /**
   * Publie `(conversationId, mountEpoch)` sur le host — **le contrat DOM** des
   * spécifications depuis #1336.
   *
   * Deux attributs, un seul écrivain : `data-canvas-host` porte la clé composée
   * rendue par `cleDeMontage` (`<conversation>#<époque>`), `data-canvas-mount-epoch`
   * la seule époque, que les spécifications lisent numériquement. Les écrire
   * ailleurs, ou séparément, les ferait diverger au premier remontage — c'est
   * exactement le miroir désynchronisé que #1334 a supprimé côté registre.
   *
   * ⚠️ Le parking, lui, porte la CONVERSATION nue (`data-canvas-park`) : il est
   * le foyer du document et survit à ses montages. Les deux attributs n'ont donc
   * volontairement pas la même forme.
   */
  private publierLIdentiteDeMontage(host: HTMLElement, mountEpoch: number): void {
    host.dataset.canvasHost = cleDeMontage({
      forme: 'conversation',
      conversationId: this.conversationId,
      mountEpoch,
    });
    host.dataset.canvasMountEpoch = String(mountEpoch);
  }

  getDomResources(): CanvasDocumentDomResources {
    if (this.dom) return this.dom;
    if (typeof document === 'undefined') {
      throw new Error(
        `Canvas DOM resources unavailable for conversationId=${this.conversationId}: `
        + 'the scope is running without a browser document. Create hosts only in the resident browser runtime.',
      );
    }
    const suffix = scopeDomSuffix(this.conversationId);
    const parking = document.createElement('div');
    parking.dataset.canvasPark = this.conversationId;
    parking.style.position = 'fixed';
    parking.style.top = '0';
    parking.style.left = '-100000px';
    parking.style.width = '900px';
    parking.style.height = '800px';
    parking.style.overflow = 'hidden';
    parking.style.pointerEvents = 'none';
    parking.setAttribute('aria-hidden', 'true');
    parking.setAttribute('inert', '');

    const host = document.createElement('div');
    const hostId = `hector-redaction-host-${suffix}`;
    host.id = hostId;
    this.publierLIdentiteDeMontage(host, this.snapshot.mountEpoch);
    host.style.width = '100%';
    host.style.height = '100%';
    parking.appendChild(host);
    document.body.appendChild(parking);

    this.dom = {
      host,
      parking,
      hostId,
      toolbarId: `hector-redaction-toolbar-${suffix}`,
      scrollId: `hector-redaction-scroll-${suffix}`,
      documentId: `hector-redaction-document-${suffix}`,
    };
    return this.dom;
  }

  query<ElementType extends Element>(selector: string): ElementType | null {
    return this.getDomResources().host.querySelector<ElementType>(selector);
  }

  claimHost(slot: HTMLElement): void {
    const { host, scrollId } = this.getDomResources();
    if (host.parentElement !== slot) slot.appendChild(host);
    const scroll = host.querySelector<HTMLElement>(`#${scrollId}`);
    const scrollTop = this.parkedScrollTop;
    if (!scroll || scrollTop === null) return;
    scroll.scrollTop = scrollTop;
    // Chromium peut recalculer le viewport après le reparenting. Réaffirmer la
    // position à la frame suivante garde A intact sans lier le scope au cycle
    // React ; la garde de parent interdit à un callback tardif de toucher B.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        if (host.parentElement === slot) scroll.scrollTop = scrollTop;
      });
    }
  }

  releaseHost(slot: HTMLElement): void {
    const { host, parking, scrollId } = this.getDomResources();
    if (host.parentElement !== slot) return;
    this.parkedScrollTop = host.querySelector<HTMLElement>(`#${scrollId}`)?.scrollTop ?? null;
    const { width, height } = slot.getBoundingClientRect();
    if (width > 0) parking.style.width = `${Math.round(width)}px`;
    if (height > 0) parking.style.height = `${Math.round(height)}px`;
    parking.appendChild(host);
  }

  isHostParked(): boolean {
    const { host, parking } = this.getDomResources();
    return host.parentElement === parking;
  }

  setWriter(writer: CanvasDocumentWriter | null): void {
    this.physical = { ...this.physical, writer };
    this.notifyPhysical();
  }

  /**
   * Déclare comment savoir si le point d'écriture de ce document écrit encore.
   *
   * La génération en flux pousse dans le point d'écriture sans passer par
   * `runCanvasWriterActivity` : le registre l'ignore. Cette sonde est donc la
   * seule façon, pour l'autosave, de ne pas exporter au milieu d'une pose
   * (#1404). Le retrait rendu n'efface que SA sonde : un démontage tardif ne
   * doit pas désarmer le montage vivant.
   */
  setWriteInFlightProbe(probe: () => boolean): () => void {
    this.writeInFlightProbe = probe;
    return () => {
      if (this.writeInFlightProbe === probe) this.writeInFlightProbe = null;
    };
  }

  isWriteInFlight(): boolean {
    return this.writeInFlightProbe?.() === true;
  }

  setNamedLetterheadResolver(resolver: CanvasNamedLetterheadResolver | null): void {
    this.physical = { ...this.physical, namedLetterheadResolver: resolver };
    this.notifyPhysical();
  }

  setZoomPercent(zoomPercent: number): void {
    // SuperDoc peut réémettre le zoom courant lorsqu'il relit sa configuration.
    // La ressource est observée avec useSyncExternalStore : republier 80 → 80
    // re-renderait le résident puis le wrapper, qui réémettrait le même zoom.
    // Un changement physique doit donc être un vrai changement de valeur.
    if (this.physical.zoomPercent === zoomPercent) return;
    this.physical = { ...this.physical, zoomPercent };
    this.notifyPhysical();
  }

  /**
   * Règle le régime de suivi de ce document, et pose aussitôt le mode
   * correspondant sur l'éditeur monté.
   *
   * Le bouton de la barre d'outils et l'outil `set_review_mode` de l'agent
   * passent tous deux par ici : une seule source, donc le bouton affiche ce que
   * l'agent vient de régler. La pose est SYNCHRONE — la confier à un effet React
   * laisserait, entre le réglage et le rendu, un éditeur qui ne fait pas encore
   * ce que le régime annonce, et le reçu de l'agent relirait l'ancien mode.
   */
  /**
   * Vrai si le peintre de l'éditeur MONTÉ est tenu pour arrêté : un plan de
   * forme refusé après une étape jouée l'arrête, et il le reste d'un appel
   * d'agent à l'autre (`canvas-agent/peintreDeLEditeur.ts`, issue 1913). Les
   * écritures de forme le lisent pour le dire dans leur reçu.
   */
  peintreEstArrete(): boolean {
    return this.peintreArrete;
  }

  /** Tient l'état du peintre à jour : appelé par les écritures de forme, qui seules savent ce que l'éditeur a répondu. */
  noterLePeintre(arrete: boolean): void {
    this.peintreArrete = arrete;
  }

  setReviewEnabled(reviewEnabled: boolean): void {
    this.physical = { ...this.physical, reviewEnabled };
    alignerLeModeSurLeRegime(this.physical.instance, reviewEnabled);
    this.notifyPhysical();
  }

  releaseMount(identity: {
    conversationId: string;
    mountEpoch: number;
  }): void {
    if (
      identity.conversationId !== this.conversationId
      || !this.accepts(identity.mountEpoch)
    ) return;
    this.mutationGeneration += 1;
    this.draftController.configureInstance(null);
    this.replaceMountResources({
      instance: null,
      editor: null,
      revision: null,
      state: 'loading',
    }, true);
  }

  remount(
    identity: CanvasDocumentMountIdentity,
    presentation?: CanvasDocumentPresentationSnapshot,
  ): CanvasDocumentScopeSnapshot {
    if (identity.conversationId !== this.conversationId) {
      throw new Error(
        `CanvasDocumentScope remount refused: scope conversationId=${this.conversationId} `
        + `cannot receive conversationId=${identity.conversationId}. Resolve the target scope from the directory.`,
      );
    }
    if (identity.mountEpoch === EPOQUE_AVANT_MONTAGE) {
      throw new Error(
        `CanvasDocumentScope remount refused for conversationId=${this.conversationId}: `
        + `mountEpoch=${EPOQUE_AVANT_MONTAGE} designates no mount since #1336. `
        + 'Allocate an epoch with canvasSessionRegistry.advanceMountEpoch before remounting this scope.',
      );
    }
    const ownerDecision = this.draftController.bindOwner(identity.conversationId);
    if (!ownerDecision.accepted) {
      throw new Error(
        `CanvasDocumentScope remount refused for conversationId=${this.conversationId}: `
        + `draft owner=${ownerDecision.expectedConversationId ?? 'null'} `
        + `cannot become conversationId=${ownerDecision.receivedConversationId ?? 'null'}. `
        + 'Resolve the document session already owned by that conversation.',
      );
    }
    const previousInstance = this.snapshot.instance;
    this.mutationGeneration += 1;
    this.coldHydratedDraftId = null;
    // L'éditeur de l'époque précédente est détruit : son peintre arrêté ne dit rien du suivant.
    this.peintreArrete = false;
    this.draftController.configureInstance(null);
    try {
      // Le cleanup React de l'ancienne époque devient volontairement un no-op
      // (il ne passe plus `accepts`). Détruire ici évite donc qu'un SuperDoc
      // orphelin reste vivant après le commit du nouveau montage.
      previousInstance?.destroy?.();
    } catch {
      // La destruction fournisseur ne doit pas transformer un remplacement
      // déjà validé en rollback partiel : l'ancienne époque est invalide.
    }
    // La présentation doit être installée AVANT la notification de changement
    // de ressources : un résident qui observe la nouvelle époque ne peut pas
    // voir la présentation de l'époque précédente, même un instant.
    if (presentation) this.presentation = clonePresentation(presentation);
    this.snapshot = { ...this.snapshot, ...identity };
    this.liesALEpoque = { instance: false, editor: false };
    // La nouvelle époque remplace le document : ce qu'il affichait lui est antérieur.
    this.borneDeLEpoque = this.travailAffiche;
    // Le remontage change la version : les lecteurs de présentation doivent
    // relire, y compris ceux qui n'observaient que la présentation.
    this.notifyPresentation();
    if (this.dom) this.publierLIdentiteDeMontage(this.dom.host, identity.mountEpoch);
    this.replaceMountResources({
      instance: null,
      editor: null,
      revision: null,
      state: 'loading',
    }, true);
    return this.getSnapshot();
  }

  markReady(mountEpoch: number, instance: SuperDocInstance): Promise<void> {
    if (!this.accepts(mountEpoch)) return Promise.resolve();
    // La même époque peut être re-signalée après un render de projection. Le
    // runtime doit conserver ce raccordement, sans republier une ressource qui
    // n'a pas changé : ses abonnés React ne verraient autrement qu'un faux
    // changement et pourraient redemander le même signal en boucle.
    if (this.snapshot.instance === instance) return this.promotion ?? Promise.resolve();
    this.liesALEpoque = { ...this.liesALEpoque, instance: true };
    // Une instance neuve naît en `editing`, quel que soit le régime choisi par
    // l'avocat. Le mode se pose ICI, à l'arrivée de l'instance — premier montage
    // ou document remplacé — et avant la promotion `ready` qui
    // démasque le document : le bouton ne peut alors jamais afficher un suivi que
    // les frappes n'ont pas (#1838).
    alignerLeModeSurLeRegime(instance, this.physical.reviewEnabled);
    // Une instance neuve a son propre peintre, qui tourne.
    this.peintreArrete = false;
    this.replaceMountResources({ instance });
    return this.tryPromote();
  }

  markEditor(mountEpoch: number, editor: SuperDocRuntimeEditor): Promise<void> {
    if (!this.accepts(mountEpoch)) return Promise.resolve();
    if (this.snapshot.editor === editor) return this.promotion ?? Promise.resolve();
    this.liesALEpoque = { ...this.liesALEpoque, editor: true };
    this.replaceMountResources({ editor });
    return this.tryPromote();
  }

  /**
   * D1 de #1337 (confirmée par le porteur le 2026-09-10) — cette ressource
   * est-elle un SECOND éditeur, ou une seconde instance, pour l'époque courante ?
   *
   * Un éditeur neuf est un montage neuf : `(conversationId, mountEpoch)` n'est
   * une identité que s'il désigne UN éditeur physique. Or SuperDoc reconstruit
   * son éditeur sous le même hôte quand les données du document changent, et un
   * hôte React recréé relâche (`releaseMount`) puis relie un éditeur vierge —
   * deux cas où l'époque ne bougeait pas. Tant qu'elle ne bouge pas, les gardes
   * qui la comparent (file de mutations, ré-assertion de la chaîne agent,
   * `settledMounts`) restent muettes.
   *
   * ⚠️ Répond à la PREUVE PHYSIQUE — une ressource liée —, jamais à une
   * intention : allouer une époque avant qu'un éditeur existe reproduirait la
   * session « loading à vie » de #857. La première instance et le premier
   * éditeur d'une époque ne comptent pas ; la même ressource ré-émise non plus.
   */
  incarneUnAutreEditeur(
    mountEpoch: number,
    ressource: { readonly instance: SuperDocInstance } | { readonly editor: SuperDocRuntimeEditor },
  ): boolean {
    if (!this.accepts(mountEpoch)) return false;
    if ('instance' in ressource) {
      return this.liesALEpoque.instance && this.snapshot.instance !== ressource.instance;
    }
    return this.liesALEpoque.editor && this.snapshot.editor !== ressource.editor;
  }

  async enqueueMutation<T>(operation: () => Promise<T> | T): Promise<T> {
    if (this.snapshot.state === 'disposed') {
      throw new Error(
        `Canvas mutation refused for conversationId=${this.conversationId}: `
        + 'the document scope has been disposed. Resolve and mount a live scope before retrying.',
      );
    }
    const expectedGeneration = this.mutationGeneration;
    const queued = this.mutationQueue.catch(() => undefined).then(() => {
      if (
        this.snapshot.state === 'disposed'
        || this.mutationGeneration !== expectedGeneration
      ) {
        throw new Error(
          `Canvas mutation abandoned for conversationId=${this.conversationId}: `
          + `queuedGeneration=${expectedGeneration} currentGeneration=${this.mutationGeneration} `
          + `state=${this.snapshot.state}. The document was remounted or purged before this queued operation began.`,
        );
      }
      return operation();
    });
    this.mutationQueue = queued;
    return queued;
  }

  refreshRevision(revision: string): CanvasDocumentScopeSnapshot | null {
    if (this.snapshot.state !== 'ready') return null;
    this.snapshot = { ...this.snapshot, revision };
    this.notifySnapshot();
    return this.getSnapshot();
  }

  async syncRevisionFromEditor(): Promise<string | null> {
    if (this.snapshot.state !== 'ready' || !this.snapshot.editor) return null;
    const expectedEpoch = this.snapshot.mountEpoch;
    const expectedEditor = this.snapshot.editor;
    try {
      const revision = await readRevision(expectedEditor);
      if (
        !revision
        || !this.accepts(expectedEpoch)
        || this.snapshot.state !== 'ready'
        || this.snapshot.editor !== expectedEditor
      ) return null;
      this.snapshot = { ...this.snapshot, revision };
      this.notifySnapshot();
      return revision;
    } catch {
      return null;
    }
  }

  async waitForPromotion(): Promise<void> {
    let observed: Promise<void> | null = null;
    while (this.promotion !== null && this.promotion !== observed) {
      observed = this.promotion;
      await observed.catch(() => undefined);
    }
  }

  dispose(_reason: string): void {
    this.disposePhysicalResources();
  }

  /**
   * Releases only the provider-owned residence. The directory keeps the draft
   * controller and presentation so this logical document can be hydrated on a
   * new mount epoch. Account purge still calls terminal `dispose`.
   */
  cool(_reason: string): void {
    this.disposePhysicalResources();
  }

  private disposePhysicalResources(): void {
    if (this.snapshot.state === 'disposed') return;
    this.mutationGeneration += 1;
    this.draftController.configureInstance(null);
    const instance = this.snapshot.instance;
    try {
      instance?.destroy?.();
    } catch {
      // Provider cleanup is best effort, while the logical checkpoint remains.
    }
    this.replaceMountResources({
      instance: null,
      editor: null,
      revision: null,
      state: 'disposed',
    }, true);
    for (const disposer of [...this.disposers]) {
      try { disposer(); } catch { /* release remaining physical resources */ }
    }
    this.disposers.clear();
    this.physicalListeners.clear();
    this.presentationListeners.clear();
    this.snapshotListeners.clear();
    this.dom?.host.remove();
    this.dom?.parking.remove();
    this.dom = null;
    this.parkedScrollTop = null;
    this.writeInFlightProbe = null;
  }

  private accepts(mountEpoch: number): boolean {
    return this.snapshot.state !== 'disposed' && this.snapshot.mountEpoch === mountEpoch;
  }

  private notifyPhysical(): void {
    for (const listener of [...this.physicalListeners]) {
      try { listener(); } catch { /* one consumer cannot interrupt account purge */ }
    }
  }

  private notifyPresentation(): void {
    for (const listener of [...this.presentationListeners]) {
      try { listener(); } catch { /* one consumer cannot interrupt account purge */ }
    }
  }

  private notifySnapshot(): void {
    const snapshot = this.getSnapshot();
    for (const listener of [...this.snapshotListeners]) {
      try { listener(snapshot); } catch { /* one consumer cannot interrupt account purge */ }
    }
  }

  /** Met à jour ensemble les deux projections d'une même ressource de montage. */
  private replaceMountResources(
    patch: Partial<Pick<CanvasDocumentScopeSnapshot, 'instance' | 'editor' | 'revision'>> & { state?: 'loading' | 'disposed' },
    clearWriter = false,
  ): void {
    // La cause d'un échec ne survit pas au montage qui a échoué.
    this.snapshot = { ...this.snapshot, ...patch, ...(patch.state ? { failure: null } : {}) };
    this.physical = {
      ...this.physical,
      instance: this.snapshot.instance,
      editor: this.snapshot.editor,
      ...(clearWriter ? { writer: null } : {}),
    };
    this.notifyPhysical();
    this.notifySnapshot();
  }

  private tryPromote(): Promise<void> {
    this.promotion = (this.promotion ?? Promise.resolve())
      .then(() => this.promoteIfPossible(), () => this.promoteIfPossible());
    return this.promotion;
  }

  private async promoteIfPossible(): Promise<void> {
    if (this.snapshot.state !== 'loading' || !this.snapshot.instance || !this.snapshot.editor) return;
    const expectedEpoch = this.snapshot.mountEpoch;
    const expectedEditor = this.snapshot.editor;
    try {
      const revision = await readRevision(expectedEditor);
      if (!this.accepts(expectedEpoch) || this.snapshot.editor !== expectedEditor) return;
      this.snapshot = revision
        ? { ...this.snapshot, revision, state: 'ready' }
        : { ...this.snapshot, revision: null, state: 'failed', failure: decrireLEchecDuMontage('sans-revision') };
      this.draftController.configureInstance(revision ? this.snapshot.instance : null);
      this.notifySnapshot();
    } catch (erreur) {
      if (!this.accepts(expectedEpoch) || this.snapshot.editor !== expectedEditor) return;
      // La cause rendue à l'agent est bornée : l'erreur entière, avec sa pile, ne reste lisible qu'ici.
      console.error(
        `[canvas] mount conversationId=${this.snapshot.conversationId} mountEpoch=${expectedEpoch} failed: `
        + 'neither editor.doc.info({}) nor editor.doc.blocks.list() returned a document revision.',
        erreur,
      );
      this.snapshot = { ...this.snapshot, revision: null, state: 'failed', failure: decrireLEchecDuMontage({ erreur }) };
      this.notifySnapshot();
    }
  }
}

/** Annuaire de ressources physiques, exclusivement indexé par session documentaire. */
export interface CanvasDocumentScopeDirectoryOptions {
  readonly draftControllerFactory?: (
    context: CanvasDraftSessionContext,
    dependencies: Pick<CanvasDraftControllerDependencies, 'isWriting'>,
  ) => CanvasDraftSessionController;
  /**
   * Activité `writer`/`mutation` du registre pour une session (write_markdown,
   * génération orchestrateur, verbes agent). Composée avec la sonde du point
   * d'écriture du scope pour reporter l'autosave (#1404).
   */
  readonly isSessionWriting?: (conversationId: string, options?: import('./canvasDraftController').CanvasCheckpointOptions) => boolean;
}

/**
 * Ce qui reste d'un document quand il n'a plus de scope — et RIEN de plus.
 *
 * ⚠️ `identiteHorsMontage` et `presentation` sont le foyer FROID de ces deux
 * faits, jamais une copie de ce que porte un scope vivant (#1334). Tant qu'un
 * scope existe, `getLogicalSeed` lit le scope et ces deux champs ne sont lus par
 * personne ; ils ne sont écrits qu'aux trois instants où aucun scope ne fait
 * autorité — la préparation d'un brouillon restauré, le refroidissement, et la
 * création du dossier logique lui-même.
 *
 * `mount()` écrivait `identity` à chaque montage, et c'était bien une copie :
 * redondante quand elle était juste, et **périmée sur le chemin de remontage**,
 * qui rendait tôt sans jamais la réécrire. Elle n'a été rattrapée que parce que
 * `cool()` la refait — un correctif par accident, sur un champ dont personne
 * n'avait besoin à cet instant.
 */
interface CanvasDocumentLogicalResources {
  readonly draftController: CanvasDraftSessionController;
  identiteHorsMontage: CanvasDocumentMountIdentity;
  presentation: CanvasDocumentPresentationSnapshot | null;
  /** Le travail qu'affichait le scope refroidi — la borne du scope réhydraté. */
  travailDejaAffiche?: string | null;
}

export class CanvasDocumentScopeDirectory {
  private readonly scopes = new Map<string, CanvasDocumentScope>();
  private readonly logicalResources = new Map<string, CanvasDocumentLogicalResources>();
  private readonly draftControllerFactory: NonNullable<CanvasDocumentScopeDirectoryOptions['draftControllerFactory']>;
  private readonly isSessionWriting: NonNullable<CanvasDocumentScopeDirectoryOptions['isSessionWriting']>;

  constructor(options: CanvasDocumentScopeDirectoryOptions = {}) {
    this.draftControllerFactory = options.draftControllerFactory
      ?? ((context, dependencies) => new CanvasDraftSessionController(context, dependencies));
    this.isSessionWriting = options.isSessionWriting ?? (() => false);
  }

  /**
   * Le signal « écriture en cours » d'UNE session exacte : jamais le canvas
   * visible, jamais un état global — A qui écrit ne reporte pas l'autosave de B.
   * Le scope est relu à chaque appel : il peut être refroidi puis remonté.
   */
  private writeDependencies(conversationId: string): Pick<CanvasDraftControllerDependencies, 'isWriting'> {
    return {
      isWriting: (options) => this.scopes.get(conversationId)?.isWriteInFlight() === true
        || this.isSessionWriting(conversationId, options),
    };
  }

  /**
   * ⚠️ **Un scope EST un montage, donc il exige une époque allouée** (#1336).
   * `prepareLogicalSession` reste le chemin de la session PRÉPARÉE mais non
   * montée, où l'époque vaut légitimement `EPOQUE_AVANT_MONTAGE` ; monter là
   * publierait un host que `(conversationId, mountEpoch)` ne désigne pas, et le
   * DOM est depuis ce lot le contrat que les spécifications interrogent.
   */
  mount(
    identity: CanvasDocumentMountIdentity,
    presentation?: CanvasDocumentPresentationSnapshot,
  ): CanvasDocumentScope {
    if (identity.mountEpoch === EPOQUE_AVANT_MONTAGE) {
      throw new Error(
        `Canvas document scope mount refused for conversationId=${identity.conversationId}: `
        + `mountEpoch=${EPOQUE_AVANT_MONTAGE} designates no mount since #1336. `
        + 'Allocate an epoch with canvasSessionRegistry.advanceMountEpoch (or prepareLogicalSession '
        + 'if this document must stay cold) before mounting it.',
      );
    }
    const existing = this.scopes.get(identity.conversationId);
    if (existing) {
      existing.remount(identity, presentation);
      return existing;
    }
    const logical = this.logicalResources.get(identity.conversationId);
    const draftController = logical?.draftController
      ?? this.draftControllerFactory({
        conversationId: identity.conversationId,
        caseId: null,
        kind: 'generated',
        title: CANVAS_DRAFT_DEFAULT_TITLE,
        sourceDocumentId: null,
      }, this.writeDependencies(identity.conversationId));
    const resources: CanvasDocumentLogicalResources = logical ?? {
      draftController,
      identiteHorsMontage: { ...identity },
      presentation: null,
    };
    // PAS de réécriture de `identiteHorsMontage` ici : à partir de cette ligne
    // c'est le scope qui fait autorité, et `cool()` la reposera si le document
    // redevient froid.
    this.logicalResources.set(identity.conversationId, resources);
    const created = new CanvasDocumentScope(identity, draftController);
    created.heriterDuTravailDejaAffiche(resources.travailDejaAffiche ?? null);
    if (presentation ?? resources.presentation) {
      created.capturePresentation(presentation ?? resources.presentation!);
    }
    this.scopes.set(identity.conversationId, created);
    return created;
  }

  get(conversationId: string): CanvasDocumentScope | null {
    return this.scopes.get(conversationId) ?? null;
  }

  getDraftController(conversationId: string): CanvasDraftSessionController | null {
    return this.logicalResources.get(conversationId)?.draftController ?? null;
  }

  getLogicalSeed(conversationId: string): CanvasDocumentLogicalSeed | null {
    const scope = this.scopes.get(conversationId);
    if (scope) {
      const snapshot = scope.getSnapshot();
      return {
        identity: {
          conversationId: snapshot.conversationId,
          mountEpoch: snapshot.mountEpoch,
          documentId: snapshot.documentId,
        },
        presentation: scope.getPresentation(),
      };
    }
    const logical = this.logicalResources.get(conversationId);
    return logical ? {
      identity: { ...logical.identiteHorsMontage },
      presentation: logical.presentation ? { ...logical.presentation } : null,
    } : null;
  }

  /**
   * Prépare les ressources logiques d'un brouillon sans monter SuperDoc.
   * La présentation reste privée à la session jusqu'à sa sélection effective.
   */
  prepareLogicalSession(input: {
    identity: CanvasDocumentMountIdentity;
    presentation: CanvasDocumentPresentationSnapshot;
    draftContext: CanvasDraftSessionContext;
  }): CanvasDraftSessionController {
    const existing = this.logicalResources.get(input.identity.conversationId);
    const draftController = existing?.draftController
      ?? this.draftControllerFactory(
        input.draftContext,
        this.writeDependencies(input.identity.conversationId),
      );
    const ownerDecision = draftController.configureContext(input.draftContext);
    if (!ownerDecision.accepted) {
      throw new Error(
        `Canvas logical preparation refused for conversationId=${input.identity.conversationId}: `
        + `controllerConversationId=${ownerDecision.expectedConversationId ?? 'null'} `
        + `requestedConversationId=${ownerDecision.receivedConversationId ?? 'null'}. `
        + 'Resolve the session owned by the requested conversation before retrying.',
      );
    }
    this.logicalResources.set(input.identity.conversationId, {
      draftController,
      identiteHorsMontage: { ...input.identity },
      presentation: { ...input.presentation },
    });
    this.scopes.get(input.identity.conversationId)?.capturePresentation(input.presentation);
    return draftController;
  }

  cool(conversationId: string, reason: string): boolean {
    const scope = this.scopes.get(conversationId);
    const logical = this.logicalResources.get(conversationId);
    if (!scope || !logical) return false;
    const snapshot = scope.getSnapshot();
    // Le scope va disparaître : c'est ici, et seulement ici, que son identité
    // vivante redevient le foyer froid du document logique.
    logical.identiteHorsMontage = {
      conversationId: snapshot.conversationId,
      mountEpoch: snapshot.mountEpoch,
      documentId: snapshot.documentId,
    };
    logical.presentation = scope.getPresentation();
    logical.travailDejaAffiche = scope.travailDejaAffiche();
    scope.cool(reason);
    this.scopes.delete(conversationId);
    return true;
  }

  requireReady(conversationId: string): CanvasDocumentScopeSnapshot {
    const snapshot = this.get(conversationId)?.getSnapshot() ?? null;
    if (
      !snapshot
      || snapshot.state !== 'ready'
      || !snapshot.instance
      || !snapshot.editor
      || !snapshot.revision
    ) {
      throw new Error(
        `Canvas document scope is not ready for conversationId=${conversationId}: `
        + `state=${snapshot?.state ?? 'missing'} revision=${snapshot?.revision ?? 'none'} `
        + `hasInstance=${Boolean(snapshot?.instance)} hasEditor=${Boolean(snapshot?.editor)}. `
        + 'Mount that exact routed session and wait for its ready/editor signals before retrying.',
      );
    }
    return snapshot;
  }

  dispose(conversationId: string, reason: string): boolean {
    const scope = this.scopes.get(conversationId);
    const logical = this.logicalResources.get(conversationId);
    if (!scope && !logical) return false;
    scope?.dispose(reason);
    this.scopes.delete(conversationId);
    logical?.draftController.dispose();
    this.logicalResources.delete(conversationId);
    return true;
  }

  purgeAll(reason: string): number {
    const scopes = [...this.scopes.values()];
    // Invalidate timers, queued PUTs and late ACKs before any SuperDoc teardown
    // can yield control to another authenticated account.
    for (const resources of this.logicalResources.values()) resources.draftController.dispose();
    this.logicalResources.clear();
    for (const scope of scopes) scope.dispose(reason);
    this.scopes.clear();
    return scopes.length;
  }
}
